// filepath: apps/web/src/features/admin/templates/export-import/useTemplateBundleImportPreview.ts
//
// FR-UI-ADM-13 AC-2 — pure client-side hook for parsing a
// user-selected `.ascoa-template` / `.zip` file via FileReader
// + JSZip. NO network call (per Q1 — the actual import POST
// is operator-mode-only and is OUT OF SCOPE for this FR).
//
// SCOPE DECISION (Q1, embedded in 01-requirement-validation.md
// `architectural_placement.scope_decisions[].id="Q1-auth-model"`):
//   The SPA does NOT POST to /admin/templates/import. The
//   FileReader + JSZip parse path is purely informational: it
//   extracts the `manifest.json` entry from the zip archive,
//   parses it, and runs a minimal local validation (missing
//   fields + cross-publisher-kind guard). The "Submit import"
//   button in `ImportDialog` is rendered DISABLED with a
//   tooltip pointing to the operator console.
//
// INV-5 CONTRACT (TRANSIENT ONLY):
//   The parsed manifest is read by `ImportDialog` to render the
//   validation preview + the Replace prompt, then DISCARDED.
//
//   The parsed `manifest_json` MUST NOT be:
//     - persisted in component state beyond the dialog's open
//       lifetime (the hook returns `null` after the consumer is
//       done; the dialog closes → local state goes out of scope);
//     - written to the TanStack Query cache (this hook does not
//       call `useQuery` — it is a `useState`-backed parser);
//     - written to localStorage / sessionStorage;
//     - serialized into the console or telemetry.
//
//   The file header documents this contract for SecurityReviewer
//   verification — the static guard
//   (apps/web/src/runtime/staticGuards.test.ts) catches
//   source-string leaks but cannot catch runtime state leaks.
//
// PARSE PATH:
//   File (the user-selected file object)
//     → FileReader.readAsArrayBuffer
//     → JSZip.loadAsync(arrayBuffer)
//     → zip.file("manifest.json").async("string")
//     → JSON.parse(manifestJson)
//     → [local validation: required fields + publisher_kind]
//     → ImportPreview | TemplateBundleImportPreviewError
//
//   No `fetch()` call is made at any step. The unit test asserts
//   `globalThis.fetch` is NOT invoked (AC-2 "no network call"
//   invariant).

import { useCallback, useRef, useState } from "react";
import JSZip from "jszip";
import type { ImportPreview, TemplateBundleImportPreviewError } from "./types";

/**
 * `UseTemplateBundleImportPreviewResult` — the public contract
 * of the hook.
 *
 *   - `preview`: the parsed manifest, or `null` if no file has
 *     been picked yet / the parse failed.
 *   - `error`: the typed error union, or `null` if the last
 *     parse succeeded or no file has been picked.
 *   - `parse(file)`: kicks off the parse; resolves to `void` —
 *     the consumer reads `preview` / `error` via the next render.
 *   - `reset()`: clears the preview + error state (called when
 *     the dialog closes).
 *   - `isParsing`: `true` while the FileReader + JSZip parse is
 *     in flight.
 */
export interface UseTemplateBundleImportPreviewResult {
  preview: ImportPreview | null;
  error: TemplateBundleImportPreviewError | null;
  parse: (file: File) => void;
  reset: () => void;
  isParsing: boolean;
}

/**
 * Required manifest fields per FR-TPL-1 (subset — the SPA
 * local validation only needs the four fields below; the full
 * server-side schema is NOT mirrored here).
 */
const REQUIRED_FIELDS = [
  "template_id",
  "version",
  "publisher_kind",
  "description",
] as const;

/**
 * useTemplateBundleImportPreview — pure client-side hook.
 *
 * Backed by `useState` (NOT `useQuery`); no TanStack Query
 * cache is touched. The hook does not depend on the tenant
 * (no cross-tenant scoping required — the parsed manifest is
 * tenant-agnostic and is never persisted).
 */
export function useTemplateBundleImportPreview(): UseTemplateBundleImportPreviewResult {
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<TemplateBundleImportPreviewError | null>(
    null,
  );
  const [isParsing, setIsParsing] = useState(false);
  // Guard against a stale async resolution — if the consumer
  // picks a second file while the first is still parsing, the
  // first parse's `setState` calls MUST NOT clobber the
  // second parse's result.
  const parseTokenRef = useRef(0);

  const parse = useCallback((file: File) => {
    const token = ++parseTokenRef.current;
    setIsParsing(true);
    setError(null);
    setPreview(null);

    if (typeof file.size === "number" && file.size === 0) {
      setIsParsing(false);
      setError({
        kind: "file_pick_failed",
        message: "empty file",
      });
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      if (token !== parseTokenRef.current) return;
      setIsParsing(false);
      setError({
        kind: "file_pick_failed",
        message: "file could not be read",
      });
    };
    reader.onload = async () => {
      // Bail if a newer parse started while we were waiting on
      // the FileReader (the consumer picked a second file).
      if (token !== parseTokenRef.current) return;
      const buf = reader.result;
      if (!(buf instanceof ArrayBuffer)) {
        setIsParsing(false);
        setError({
          kind: "file_pick_failed",
          message: "unexpected reader result",
        });
        return;
      }
      try {
        const zip = await JSZip.loadAsync(buf);
        const manifestEntry = zip.file("manifest.json");
        if (!manifestEntry) {
          setIsParsing(false);
          setError({
            kind: "manifest_missing",
            message: "manifest.json not found in archive",
          });
          return;
        }
        const manifestText = await manifestEntry.async("string");
        let parsed: unknown;
        try {
          parsed = JSON.parse(manifestText);
        } catch {
          setIsParsing(false);
          setError({
            kind: "manifest_invalid_json",
            message: "manifest.json is not valid JSON",
          });
          return;
        }
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setIsParsing(false);
          setError({
            kind: "manifest_invalid_json",
            message: "manifest.json is not a JSON object",
          });
          return;
        }
        const obj = parsed as Record<string, unknown>;
        const missing: string[] = [];
        for (const field of REQUIRED_FIELDS) {
          if (typeof obj[field] !== "string" || obj[field] === "") {
            missing.push(field);
          }
        }
        if (missing.length > 0) {
          setIsParsing(false);
          setError({
            kind: "manifest_incomplete",
            message: `manifest missing fields: ${missing.join(", ")}`,
            missingFields: missing,
          });
          return;
        }
        // Cross-publisher-kind guard mirror — mirrors the backend's
        // import.go:191 rejection. Surfaces the constraint without
        // the user having to attempt the operator console upload.
        const publisher_kind = obj["publisher_kind"] as string;
        if (publisher_kind !== "platform") {
          setIsParsing(false);
          setError({
            kind: "cross_publisher_kind",
            message:
              "Only platform-published bundles can be imported by operators",
          });
          return;
        }
        // Parse succeeded — cast through the validated required
        // fields (all four are confirmed `string` and non-empty
        // above).
        const next: ImportPreview = {
          template_id: obj["template_id"] as string,
          version: obj["version"] as string,
          publisher_kind: "platform",
          description: obj["description"] as string,
        };
        setIsParsing(false);
        setPreview(next);
      } catch {
        // JSZip or FileReader failure path (corrupt archive,
        // out-of-memory, etc.).
        if (token !== parseTokenRef.current) return;
        setIsParsing(false);
        setError({
          kind: "file_pick_failed",
          message: "archive could not be parsed",
        });
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const reset = useCallback(() => {
    // Bump the token so any in-flight parse resolves into the
    // void.
    parseTokenRef.current += 1;
    setPreview(null);
    setError(null);
    setIsParsing(false);
  }, []);

  return { preview, error, parse, reset, isParsing };
}
